/**
 * GameStateManager - Handles game progression, saving, and loading
 * 
 * Tracks:
 * - Current world and level
 * - Unlocked levels
 * - Completed levels
 * - High scores per level
 */

import { WORLDS } from '../config/game';

export interface LevelProgress {
  completed: boolean;
  highScore?: number;
  bestTime?: number;
  stars?: number; // 0-3 stars based on performance
}

export interface GameState {
  currentWorld: number;
  currentLevel: string;
  unlockedLevels: string[];
  completedLevels: Record<string, LevelProgress>;
  totalStars: number;
}

const STORAGE_KEY = 'silver_saga_progress';

// Level order for progression
const LEVEL_ORDER: string[] = [];
WORLDS.forEach(world => {
  world.levels.forEach(level => {
    LEVEL_ORDER.push(level.id);
  });
});

class GameStateManagerClass {
  private state: GameState;

  constructor() {
    this.state = this.loadState();
  }

  private getDefaultState(): GameState {
    return {
      currentWorld: 1,
      currentLevel: '1-1',
      unlockedLevels: ['1-1'], // First level always unlocked
      completedLevels: {},
      totalStars: 0,
    };
  }

  private loadState(): GameState {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle new fields
        const merged = { ...this.getDefaultState(), ...parsed };
        
        // Ensure unlockedLevels always includes '1-1'
        if (!merged.unlockedLevels || !Array.isArray(merged.unlockedLevels)) {
          merged.unlockedLevels = ['1-1'];
        } else if (!merged.unlockedLevels.includes('1-1')) {
          merged.unlockedLevels.unshift('1-1');
        }
        
        // Ensure completedLevels is an object
        if (!merged.completedLevels || typeof merged.completedLevels !== 'object') {
          merged.completedLevels = {};
        }
        
        console.log('[GameState] Loaded:', merged);
        return merged;
      }
    } catch (e) {
      console.warn('Failed to load game state, resetting:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    console.log('[GameState] Using default state');
    return this.getDefaultState();
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.warn('Failed to save game state:', e);
    }
  }

  // Getters
  getCurrentWorld(): number {
    return this.state.currentWorld;
  }

  getCurrentLevel(): string {
    return this.state.currentLevel;
  }

  getUnlockedLevels(): string[] {
    return [...this.state.unlockedLevels];
  }

  isLevelUnlocked(levelId: string): boolean {
    return this.state.unlockedLevels.includes(levelId);
  }

  isLevelCompleted(levelId: string): boolean {
    return this.state.completedLevels[levelId]?.completed ?? false;
  }

  getLevelProgress(levelId: string): LevelProgress | null {
    return this.state.completedLevels[levelId] ?? null;
  }

  getTotalStars(): number {
    return this.state.totalStars;
  }

  // Check if a world is unlocked (first level of world is unlocked)
  isWorldUnlocked(worldId: number): boolean {
    const world = WORLDS.find(w => w.id === worldId);
    if (!world || !world.levels || world.levels.length < 1) return false;
    return this.isLevelUnlocked(world.levels[0].id);
  }

  // Get progress for a specific world
  getWorldProgress(worldId: number): { completed: number; total: number } {
    const world = WORLDS.find(w => w.id === worldId);
    if (!world) return { completed: 0, total: 0 };
    
    const completed = world.levels.filter(l => 
      this.isLevelCompleted(l.id)
    ).length;
    
    return { completed, total: world.levels.length };
  }

  // Actions
  setCurrentLevel(levelId: string): void {
    this.state.currentLevel = levelId;
    
    // Update current world based on level
    const worldNum = parseInt(levelId.split('-')[0]);
    if (!isNaN(worldNum)) {
      this.state.currentWorld = worldNum;
    }
    
    this.saveState();
  }

  completeLevel(levelId: string, progress?: Partial<LevelProgress>): void {
    // Mark as completed
    const existing = this.state.completedLevels[levelId] || {};
    this.state.completedLevels[levelId] = {
      ...existing,
      ...progress,
      completed: true,
    };

    // Calculate total stars
    this.state.totalStars = Object.values(this.state.completedLevels)
      .reduce((sum, p) => sum + (p.stars ?? 0), 0);

    // Unlock next level
    const nextLevel = this.getNextLevel(levelId);
    if (nextLevel && !this.state.unlockedLevels.includes(nextLevel)) {
      this.state.unlockedLevels.push(nextLevel);
    }

    this.saveState();
  }

  getNextLevel(currentLevelId: string): string | null {
    const currentIndex = LEVEL_ORDER.indexOf(currentLevelId);
    if (currentIndex === -1 || currentIndex >= LEVEL_ORDER.length - 1) {
      return null;
    }
    return LEVEL_ORDER[currentIndex + 1];
  }

  // Get level name from ID
  getLevelName(levelId: string): string {
    for (const world of WORLDS) {
      const level = world.levels.find(l => l.id === levelId);
      if (level) return level.name;
    }
    return levelId;
  }

  // Get the scene key for a level ID
  getLevelSceneKey(levelId: string): string {
    const sceneMap: Record<string, string> = {
      '1-1': 'BlanketBossScene',
      '1-2': 'ClothesChaoScene',
      '1-3': 'MissingShoeScene',
      '1-B': 'MorningTitanScene',
      // Future levels will be added here
      '2-1': 'KindergartenScene',
      '2-2': 'HomeworkHydraScene',
      '2-3': 'FriendshipTrialsScene',
      '2-B': 'InstitutionBossScene',
      '3-1': 'VegetableVendettaScene',
      '3-2': 'SnackSiegeScene',
      '3-3': 'RestaurantScene',
      '3-B': 'DinnerTitanScene',
      '4-1': 'SoapyShowdownScene',
      '4-2': 'ShampooStandoffScene',
      '4-3': 'TeethWarScene',
      '4-B': 'CleanlinessBossScene',
      '5-1': 'OneMoreStoryScene',
      '5-2': 'WaterBathroomScene',
      '5-3': 'MidnightVisitorScene',
      '5-B': 'SandmanBossScene',
      '6-F': 'FamilyDinnerFinaleScene',
    };
    return sceneMap[levelId] ?? 'BlanketBossScene';
  }

  // Get background asset key for a level
  getLevelBackgroundKey(levelId: string): string {
    const bgMap: Record<string, string> = {
      '1-1': '1-1_blanket_fortress',
      '1-2': '1-2_clothes_chaos',
      '1-3': '1-3_missing_shoe',
      '1-B': '1-B_morning_titan',
      '2-1': '2-1_kindergarten',
      '2-2': '2-2_homework_hydra',
      '2-3': '2-3_friendship_trials',
      '2-B': '2-B_institution',
      '3-1': '3-1_vegetable_vendetta',
      '3-2': '3-2_snack_siege',
      '3-3': '3-3_restaurant',
      '3-B': '3-B_dinner_titan',
      '4-1': '4-1_soapy_showdown',
      '4-2': '4-2_shampoo_standoff',
      '4-3': '4-3_teeth_war',
      '4-B': '4-B_cleanliness',
      '5-1': '5-1_one_more_story',
      '5-2': '5-2_water_bathroom',
      '5-3': '5-3_midnight_visitor',
      '5-B': '5-B_sandman',
      '6-F': '6-F_family_dinner',
    };
    return bgMap[levelId] ?? '1-1_blanket_fortress';
  }

  // Reset all progress
  resetProgress(): void {
    this.state = this.getDefaultState();
    this.saveState();
  }

  // Unlock all levels (for debugging)
  unlockAllLevels(): void {
    this.state.unlockedLevels = [...LEVEL_ORDER];
    this.saveState();
  }
}

// Singleton instance
export const GameStateManager = new GameStateManagerClass();
